/**
 * Folding hand-picked photos into what the face search already found.
 *
 * Two things feed it: photos picked while browsing the whole album, and the
 * "might also be you" maybes the athlete ticked. Both ADD to the search
 * results and neither replaces them, which is the invariant that is easy to
 * get wrong and expensive when it is. An earlier version overwrote, so an
 * athlete who arrived with two matched photos and picked three while browsing
 * ended up with three, silently losing the two the search had found for them.
 */

export interface PickablePhoto {
  id: string;
  previewUrl: string | null;
  filename: string;
}

export interface MergeResult {
  matchedPhotoIds: string[];
  previews: { id: string; previewUrl: string | null; filename: string }[];
}

/**
 * Add the picked photos to the existing set, preserving order and dropping
 * anything already present. Picking a photo the search had already matched is
 * an ordinary thing to do (the grid shows it as already yours), so it has to
 * be a no-op rather than a duplicate row in the download.
 */
export function mergePickedPhotos(
  existingIds: string[],
  existingPreviews: { id: string; previewUrl: string | null; filename: string }[],
  candidates: PickablePhoto[],
  picked: ReadonlySet<string>,
): MergeResult {
  const seen = new Set(existingIds);
  const fresh: PickablePhoto[] = [];

  for (const photo of candidates) {
    if (!picked.has(photo.id) || seen.has(photo.id)) continue;
    // Guard against the same id appearing twice in the candidate list, which a
    // paginated browse can produce if a page boundary shifts between fetches.
    seen.add(photo.id);
    fresh.push(photo);
  }

  return {
    matchedPhotoIds: [...existingIds, ...fresh.map((p) => p.id)],
    previews: [
      ...existingPreviews,
      ...fresh.map((p) => ({ id: p.id, previewUrl: p.previewUrl, filename: p.filename })),
    ],
  };
}
